import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api from '../../services/api';

function money(n) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);
}

const EMPTY_PAY = {
  kind: 'nomina',
  name: '',
  amount: '',
  date: '',
  repeat: 'once',
  spread: false,
  account_id: '',
  note: '',
};

export default function FoodCashPanel({
  period,
  dateFrom,
  dateTo,
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [day, setDay] = useState('');
  const [accountForm, setAccountForm] = useState({ name: '', opening_balance: '', as_of: '' });
  const [cajaForm, setCajaForm] = useState({ opening_balance: '', as_of: '' });
  const [payForm, setPayForm] = useState(EMPTY_PAY);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await api.get('/platform/finance/food-cash', {
        params: {
          scope_module: 'food',
          period,
          date_from: period === 'custom' ? dateFrom || undefined : undefined,
          date_to: period === 'custom' ? dateTo || undefined : undefined,
        },
      });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'No se pudo cargar la caja');
    }
  }, [period, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const monthDays = useMemo(
    () => (data?.days || []).filter((row) => row.in_month),
    [data],
  );
  const simDays = useMemo(
    () => (data?.days || []).filter((row) => row.in_month || row.in_next_30),
    [data],
  );
  const selected = monthDays.find((row) => row.date === day) || null;
  const caja = data?.accounts?.[0];

  async function saveCaja(event) {
    event.preventDefault();
    if (!caja) return;
    setSaving(true);
    try {
      await api.patch(`/platform/finance/food-cash/accounts/${caja.id}`, {
        scope_module: 'food',
        opening_balance: cajaForm.opening_balance,
        as_of: cajaForm.as_of || data.today,
      }, { params: { scope_module: 'food' } });
      setCajaForm({ opening_balance: '', as_of: '' });
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  async function addAccount(event) {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post('/platform/finance/food-cash/accounts', {
        name: accountForm.name,
        opening_balance: accountForm.opening_balance,
        as_of: accountForm.as_of || data?.today,
      }, { params: { scope_module: 'food' } });
      setAccountForm({ name: '', opening_balance: '', as_of: '' });
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  async function addPayment(event) {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post('/platform/finance/food-cash/payments', {
        ...payForm,
        spread: payForm.kind === 'proveedor' && payForm.spread,
        account_id: payForm.account_id || caja?.id,
      }, { params: { scope_module: 'food' } });
      setPayForm(EMPTY_PAY);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  function dueDate(payment) {
    const today = data?.today || '';
    if (payment.repeat !== 'monthly' || !today) return payment.date;
    const dayNum = Number(String(payment.date).slice(8, 10)) || 1;
    const dim = new Date(Number(today.slice(0, 4)), Number(today.slice(5, 7)), 0).getDate();
    const day = String(Math.min(dayNum, dim)).padStart(2, '0');
    return `${today.slice(0, 7)}-${day}`;
  }

  async function markPaid(payment) {
    const date = dueDate(payment);
    setSaving(true);
    try {
      await api.patch(`/platform/finance/food-cash/payments/${payment.id}`, {
        paid_date: date,
        paid: !(payment.paid_dates || []).includes(date),
      }, { params: { scope_module: 'food' } });
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  async function removePayment(id) {
    setSaving(true);
    try {
      await api.delete(`/platform/finance/food-cash/payments/${id}`, {
        params: { scope_module: 'food' },
      });
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section id="food-cash-panel" className="card p-3 d28d-admin-surface ui-light-panel surface-readable space-y-4">
      <div>
        <h2 className="font-semibold text-sm m-0">Caja Food</h2>
        <p className="text-xs d28d-text-muted m-0 mt-1">
          Saldo al empezar el día que guardas. Desde ahí se suman los recaudos y se restan los egresos pagados.
          Lo programado (nómina y proveedores) baja el saldo proyectado, no el saldo de hoy, hasta que lo marques pagado.
        </p>
        {data && data.food_db === false ? (
          <p className="text-xs text-red-700 m-0 mt-1">
            No se leyeron las suscripciones Food. El calendario de altas y bajas queda vacío; la caja programada sí se guarda.
            {data.food_error ? ` ${data.food_error}` : ''}
          </p>
        ) : null}
      </div>
      {error ? <p className="text-sm text-red-700 m-0">{error}</p> : null}

      <div className="grid md:grid-cols-2 gap-3">
        {(data?.accounts || []).map((account) => (
          <article key={account.id} className="border border-stone-200 rounded-lg p-3">
            <p className="font-semibold m-0">{account.name}</p>
            <p className="text-xs text-slate-500 m-0">Saldo inicial {money(account.opening_balance)} desde {account.as_of}</p>
            <p className="m-0 mt-2">Hoy: <strong>{money(account.balance_today)}</strong></p>
            <p className="m-0">Proyectado al {data?.to}: <strong className={account.balance_projected < 0 ? 'text-red-700' : ''}>{money(account.balance_projected)}</strong></p>
          </article>
        ))}
      </div>

      <form onSubmit={saveCaja} className="flex flex-wrap gap-2 items-end">
        <label className="text-xs text-slate-600">
          Saldo inicial de {caja?.name || 'Caja Food'}
          <input
            className="block mt-1 border border-stone-300 rounded px-2 py-1 text-sm text-stone-900 bg-white"
            inputMode="numeric"
            value={cajaForm.opening_balance}
            onChange={(e) => setCajaForm((prev) => ({ ...prev, opening_balance: e.target.value }))}
            placeholder={caja ? String(caja.opening_balance) : '0'}
            required
          />
        </label>
        <label className="text-xs text-slate-600">
          Vale desde
          <input
            type="date"
            className="block mt-1 border border-stone-300 rounded px-2 py-1 text-sm text-stone-900 bg-white"
            value={cajaForm.as_of}
            onChange={(e) => setCajaForm((prev) => ({ ...prev, as_of: e.target.value }))}
            required
          />
        </label>
        <button type="submit" className="btn-primary text-sm" disabled={saving}>Guardar saldo</button>
      </form>

      <form onSubmit={addAccount} className="flex flex-wrap gap-2 items-end">
        <label className="text-xs text-slate-600">
          Otra cuenta
          <input
            className="block mt-1 border border-stone-300 rounded px-2 py-1 text-sm text-stone-900 bg-white"
            value={accountForm.name}
            onChange={(e) => setAccountForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Banco"
            required
          />
        </label>
        <label className="text-xs text-slate-600">
          Saldo inicial
          <input
            className="block mt-1 border border-stone-300 rounded px-2 py-1 text-sm text-stone-900 bg-white"
            inputMode="numeric"
            value={accountForm.opening_balance}
            onChange={(e) => setAccountForm((prev) => ({ ...prev, opening_balance: e.target.value }))}
            required
          />
        </label>
        <label className="text-xs text-slate-600">
          Vale desde
          <input
            type="date"
            className="block mt-1 border border-stone-300 rounded px-2 py-1 text-sm text-stone-900 bg-white"
            value={accountForm.as_of}
            onChange={(e) => setAccountForm((prev) => ({ ...prev, as_of: e.target.value }))}
            required
          />
        </label>
        <button type="submit" className="btn-secondary text-sm" disabled={saving}>Agregar cuenta</button>
      </form>

      <form onSubmit={addPayment} className="border border-stone-200 rounded-lg p-3 space-y-3">
        <div className="flex flex-wrap gap-2">
          <button type="button" className={payForm.kind === 'nomina' ? 'btn-primary text-sm' : 'btn-secondary text-sm'} onClick={() => setPayForm((prev) => ({ ...prev, kind: 'nomina', spread: false }))}>
            Nómina
          </button>
          <button type="button" className={payForm.kind === 'proveedor' ? 'btn-primary text-sm' : 'btn-secondary text-sm'} onClick={() => setPayForm((prev) => ({ ...prev, kind: 'proveedor' }))}>
            Proveedor
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <label className="text-xs text-slate-600">
            {payForm.kind === 'nomina' ? 'Persona' : 'Proveedor'}
            <input
              className="block mt-1 w-full border border-stone-300 rounded px-2 py-1 text-sm text-stone-900 bg-white"
              value={payForm.name}
              onChange={(e) => setPayForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder={payForm.kind === 'nomina' ? 'Nombre de la persona' : 'Nombre del proveedor'}
              required
            />
          </label>
          <label className="text-xs text-slate-600">
            Valor COP
            <input
              className="block mt-1 w-full border border-stone-300 rounded px-2 py-1 text-sm text-stone-900 bg-white"
              inputMode="numeric"
              value={payForm.amount}
              onChange={(e) => setPayForm((prev) => ({ ...prev, amount: e.target.value }))}
              required
            />
          </label>
          <label className="text-xs text-slate-600">
            Día de pago
            <input
              type="date"
              className="block mt-1 w-full border border-stone-300 rounded px-2 py-1 text-sm text-stone-900 bg-white"
              value={payForm.date}
              onChange={(e) => setPayForm((prev) => ({ ...prev, date: e.target.value }))}
              required
            />
          </label>
          <label className="text-xs text-slate-600">
            Sale de
            <select
              className="block mt-1 w-full border border-stone-300 rounded px-2 py-1 text-sm text-stone-900 bg-white"
              value={payForm.account_id}
              onChange={(e) => setPayForm((prev) => ({ ...prev, account_id: e.target.value }))}
            >
              {(data?.accounts || []).map((account) => (
                <option key={account.id} value={account.id}>{account.name}</option>
              ))}
            </select>
          </label>
        </div>
        <label className="text-xs text-slate-600 flex items-center gap-2">
          <input
            type="checkbox"
            checked={payForm.repeat === 'monthly'}
            onChange={(e) => setPayForm((prev) => ({ ...prev, repeat: e.target.checked ? 'monthly' : 'once' }))}
          />
          Repetir el mismo día cada mes
        </label>
        {payForm.kind === 'proveedor' ? (
          <label className="text-xs text-slate-600 flex items-center gap-2">
            <input
              type="checkbox"
              checked={payForm.spread}
              onChange={(e) => setPayForm((prev) => ({ ...prev, spread: e.target.checked }))}
            />
            Repartir el valor entre los días del mes
          </label>
        ) : null}
        <button type="submit" className="btn-primary text-sm" disabled={saving}>
          {payForm.kind === 'nomina' ? 'Programar nómina' : 'Programar proveedor'}
        </button>
      </form>

      {(data?.payments || []).length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-stone-200">
                <th className="py-1 pr-2">Tipo</th>
                <th className="py-1 pr-2">Nombre</th>
                <th className="py-1 pr-2">Valor</th>
                <th className="py-1 pr-2">Fecha</th>
                <th className="py-1"> </th>
              </tr>
            </thead>
            <tbody>
              {data.payments.map((payment) => (
                <tr key={payment.id} className="border-b border-stone-100">
                  <td className="py-1 pr-2">{payment.kind === 'nomina' ? 'Nómina' : 'Proveedor'}</td>
                  <td className="py-1 pr-2">{payment.name}{payment.repeat === 'monthly' ? ' · cada mes' : ''}{payment.spread ? ' · repartido' : ''}</td>
                  <td className="py-1 pr-2">{money(payment.amount)}</td>
                  <td className="py-1 pr-2">{payment.date}</td>
                  <td className="py-1 text-right">
                    <button type="button" className="btn-secondary text-xs mr-1" onClick={() => markPaid(payment)} disabled={saving}>
                      {(payment.paid_dates || []).includes(dueDate(payment)) ? 'Desmarcar pago' : 'Marcar pagado'}
                    </button>
                    <button type="button" className="btn-secondary text-xs" onClick={() => removePayment(payment.id)} disabled={saving}>Quitar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="text-sm m-0">Todavía no hay nómina ni proveedores programados.</p>}

      <div>
        <h3 className="font-semibold text-sm m-0">Días del mes: altas y bajas</h3>
        <p className="text-xs d28d-text-muted m-0 mt-1">
          {monthDays.reduce((sum, row) => sum + Number(row.altas || 0), 0)} altas
          {' · '}
          {monthDays.reduce((sum, row) => sum + Number(row.bajas || 0), 0)} bajas
          {' · '}
          recaudado {money(monthDays.reduce((sum, row) => sum + Number(row.collected || 0), 0))}
          . Clic en una barra para ver quién.
        </p>
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer>
            <BarChart data={monthDays} onClick={(state) => setDay(state?.activeLabel || '')}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#475569' }} tickFormatter={(v) => String(v).slice(8)} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#475569' }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="altas" name="Altas" fill="#65a30d" />
              <Bar dataKey="bajas" name="Bajas" fill="#94a3b8" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {selected ? (
          <div className="text-sm mt-2">
            <p className="m-0 font-medium">{selected.date}</p>
            <p className="m-0">Recaudado {money(selected.collected)} · Por recaudar {money(selected.expected + selected.pending)} · Egresos {money(selected.paid_out + selected.programmed_out)}</p>
            <ul className="m-0 pl-4">
              {selected.alta_rows.map((row) => <li key={`a-${row.user_id}-${row.email}`}>Alta: {row.name} {row.email}</li>)}
              {selected.baja_rows.map((row) => <li key={`b-${row.user_id}-${row.email}`}>Baja: {row.name} {row.email}</li>)}
              {selected.money_rows.map((row, idx) => (
                <li key={`m-${idx}`}>{row.label}: {row.name} {money(row.amount)}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <div>
        <h3 className="font-semibold text-sm m-0">Semana: pago del mes, por recaudar y egresos</h3>
        <p className="text-xs d28d-text-muted m-0 mt-1 mb-2">
          El verde es el pago del mes de quien sigue activo, el día en que se activó.
          El egreso oscuro de las semanas siguientes repite el mes anterior.
        </p>
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer>
            <BarChart data={data?.weeks || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#475569' }} />
              <YAxis tick={{ fontSize: 10, fill: '#475569' }} />
              <Tooltip formatter={(v) => money(v)} />
              <Legend />
              <Bar dataKey="collected" name="Pago del mes" fill="#65a30d" />
              <Bar dataKey="expected" name="Por recaudar" fill="#ea580c" />
              <Bar dataKey="out" name="Egresos" fill="#334155" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-sm m-0">Proyección de caja</h3>
        <p className="text-xs d28d-text-muted m-0 mt-1">
          Cada activo entra el día de su activación, por el valor de un mes de su plan.
          Los egresos de este mes se vuelven a poner el mes siguiente, el mismo día.
          Clic en un día para ver quién paga y qué sale.
        </p>
        {data?.outlook ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
            <article className="border border-stone-200 rounded-lg p-2">
              <p className="text-xs text-slate-500 m-0">Pago del mes</p>
              <p className="font-semibold m-0">{money(data.outlook.income_month)}</p>
              <p className="text-xs text-slate-500 m-0">{data.outlook.payers || 0} activos</p>
            </article>
            <article className="border border-stone-200 rounded-lg p-2">
              <p className="text-xs text-slate-500 m-0">Egresos del mes</p>
              <p className="font-semibold m-0">{money(data.outlook.expense_month)}</p>
            </article>
            <article className="border border-stone-200 rounded-lg p-2">
              <p className="text-xs text-slate-500 m-0">Egresos que se repiten</p>
              <p className="font-semibold m-0">{money(data.outlook.expense_projected)}</p>
            </article>
            <article className="border border-stone-200 rounded-lg p-2">
              <p className="text-xs text-slate-500 m-0">Caja al cierre del mes</p>
              <p className={`font-semibold m-0 ${Number(data.outlook.close_projected) < 0 ? 'text-red-700' : ''}`}>
                {data.outlook.close_projected == null ? '—' : money(data.outlook.close_projected)}
              </p>
            </article>
          </div>
        ) : null}
        <div className="overflow-x-auto finance-drill-table mt-3" style={{ maxHeight: 320 }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-stone-200">
                <th className="py-1 pr-2">Día</th>
                <th className="py-1 pr-2">Entra</th>
                <th className="py-1 pr-2">Por confirmar</th>
                <th className="py-1 pr-2">Sale</th>
                <th className="py-1">Caja</th>
              </tr>
            </thead>
            <tbody>
              {simDays.map((row) => {
                const projected = row.balances?.[caja?.id]?.projected;
                const before = row.balances?.[caja?.id]?.before_opening;
                const active = row.date === day;
                return (
                  <tr
                    key={row.date}
                    className={`border-b border-stone-100 cursor-pointer ${active ? 'bg-lime-50' : ''}`}
                    onClick={() => setDay(row.date)}
                  >
                    <td className="py-1 pr-2">
                      {row.date.slice(8)}/{row.date.slice(5, 7)}
                      {row.in_next_30 && !row.in_month ? ' · sig.' : ''}
                      {row.projected_out ? ' · egreso repetido' : ''}
                    </td>
                    <td className={`py-1 pr-2 ${row.collected ? 'text-emerald-800' : ''}`}>{money(row.collected)}</td>
                    <td className="py-1 pr-2">{money(row.expected + row.pending)}</td>
                    <td className="py-1 pr-2">{money(row.paid_out + row.programmed_out)}</td>
                    <td className={`py-1 ${projected < 0 ? 'text-red-700 font-semibold' : ''}`}>
                      {before || projected == null ? '—' : money(projected)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {selected ? (
          <div className="text-sm mt-2 border border-stone-200 rounded-lg p-3">
            <p className="m-0 font-medium">{selected.date}</p>
            <ul className="m-0 pl-4 mt-1">
              {selected.alta_rows.map((row) => <li key={`a-${row.user_id}-${row.email}`}>Alta: {row.name}</li>)}
              {selected.baja_rows.map((row) => <li key={`b-${row.user_id}-${row.email}`}>Baja: {row.name}</li>)}
              {selected.money_rows.map((row, idx) => (
                <li key={`m-${idx}`}>{row.label}: {row.name} {money(row.amount)}</li>
              ))}
              {!selected.money_rows.length && !selected.alta_rows.length && !selected.baja_rows.length ? (
                <li>Sin movimientos este día.</li>
              ) : null}
            </ul>
          </div>
        ) : null}
      </div>

    </section>
  );
}
